import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { MultiSelectModule } from "primeng/multiselect";
import { TagModule } from 'primeng/tag';

// Composants
import { TableFiltersBaseComponent } from 'src/app/@shared/components/abstract/table-filters-base.component';
import { DateRangeFilterComponent } from 'src/app/@shared/forms/components/date-range-filter/date-range-filter.component';

// Interfaces
import { DateRangeFilterConfig, MultiSelectFilterConfig } from 'src/app/@shared/components/abstract/table-filters-base.component';

// Modèles
import { Journal } from 'src/app/@shared/models/accounting/journal.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';

@Component({
    selector: 'app-account-entries-table-filters',
    imports: [
        FormsModule,
        BadgeModule,
        ButtonModule,
        Checkbox,
        DateRangeFilterComponent,
        MultiSelectModule,
        TagModule
    ],
    templateUrl: './account-entries-table-filters.component.html',
    styleUrl: './account-entries-table-filters.component.scss'
})
export class AccountEntriesTableFiltersComponent extends TableFiltersBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    // Options de filtrage disponibles
    public filteringOptions: any[] = [
        { label: 'Journal', value: 'journal' },
        { label: 'Période', value: 'period' },
        { label: 'Écritures exportées', value: 'extracted' },
    ];

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _journals: Journal[] = [];
    public get journals(): Journal[] {
        return this._journals;
    }
    public set journals(value: Journal[]) {
        let emptyArray: Journal[] = [];
        this._journals = emptyArray.concat(value);
        this.defineSelectedFromMultiSelectFilters('journal');
    }

    // Données des filtres multi-select
    public journalsLoaded: boolean = false;
    public journalsLoading: boolean = false;
    public journalsSelected: Journal[] = [];

    // Données des filtres date range
    public operationDateRange: [Date | null, Date | null] | null = null;

    // Données des filtres checkbox
    public extractedFilter: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Configuration des filtres date range
     ----------------------------------------------------------------------------------------*/

    protected override get dateRangeFilterConfigs(): Map<string, DateRangeFilterConfig> {
        return new Map([
            ['period', {
                property: 'accountEntry/operationDate',
                getRange: () => this.operationDateRange,
                setRange: (range: [Date | null, Date | null] | null) => this.operationDateRange = range
            }],
        ]);
    }

    /*----------------------------------------------------------------------------------------
     * Configuration des filtres multi-select
     ----------------------------------------------------------------------------------------*/

    protected override get multiSelectFilterConfigs(): Map<string, MultiSelectFilterConfig<any>> {
        return new Map([
            ['journal', {
                property: 'accountEntry/journal',
                getSelectedItems: () => this.journalsSelected,
                setSelectedItems: (items: Journal[]) => this.journalsSelected = items,
                allItems: this.journals,
                valueExtractor: (journal: Journal) => journal.id,
                matcher: (journal: Journal, value: any) => journal.id === value
            }],
        ]);
    }

    /*----------------------------------------------------------------------------------------
     * Configuration des filtres checkbox
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /**
     * Point d'extension pour construire les filtres custom (extracted)
     * Appelé par la classe de base après les filtres standard et avant la mise à jour de l'URL
     */
    protected override buildCustomFilters(): void {
        // Filtre sur les statuts (posted ou extracted si la checkbox est cochée)
        let statusFilter = new Filter({
            filters: [
                new Filter({
                    property: 'accountEntry/status',
                    value: 'posted',
                })
            ],
            and: false
        });
        if (this.extractedFilter) {
            statusFilter.filters.push(new Filter({
                property: 'accountEntry/status',
                value: 'extracted',
            }));
        }
        this.filters.push(statusFilter);
    }

    protected override loadData(): void {
        this._loadJournals();
    }

    protected override mapParams(): void {
        // Appel de la méthode de base pour gérer les filtres standard (journal, période)
        super.mapParams();

        // Lecture des filtres custom depuis l'URL
        this._defineExtractedFilterFromUrl();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Définit la valeur de extractedFilter depuis l'URL
     * Si le filtre contient 'extracted' en plus de 'posted', la checkbox est cochée
     */
    private _defineExtractedFilterFromUrl(): void {
        // Chercher si le filtre sur le statut contient 'extracted'
        const hasExtracted = this._hasStatusValue('extracted');
        this.extractedFilter = hasExtracted;
    }

    /**
     * Vérifie si les filtres contiennent une valeur spécifique pour le statut
     */
    private _hasStatusValue(statusValue: string): boolean {
        for (const filter of this.filters) {
            if (this._findStatusValue(filter, statusValue)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Recherche récursive d'une valeur de statut dans les filtres
     */
    private _findStatusValue(filter: Filter, statusValue: string): boolean {
        // Vérifier si c'est un filtre sur accountEntry/status avec la valeur recherchée
        if (filter.property === 'accountEntry.status' && filter.value === statusValue) {
            return true;
        }

        // Recherche récursive dans les sous-filtres
        if (filter.filters && filter.filters.length > 0) {
            for (const subFilter of filter.filters) {
                if (this._findStatusValue(subFilter, statusValue)) {
                    return true;
                }
            }
        }

        return false;
    }

    private _loadJournals(): void {
        if (this.journalsLoaded || this.journalsLoading) return;

        let canExportFilter = new Filter({
            property: 'canExport',
            value: true
        });
        let hasLineFilters = new Filter({
            property: 'hasEntryLine',
            value: true
        });

        this.journalsLoading = true;
        this.journalService.getList$({
            filters: [
                canExportFilter,
                hasLineFilters
            ],
            selects: [
                'code',
                'label',
            ],
        }).pipe(
            finalize(() => {
                this.journalsLoading = false;
                this.journalsLoaded = true;
            })
        ).subscribe(journals => {
            this.journals = journals;
        });
    }
}
